import { useState, useEffect } from 'react';
import axios from 'axios';
import { Bank, Trash, PencilSimple, ArrowLeft, Check, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

export const CreditsDetailView = ({ onBack }) => {
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCredit, setEditingCredit] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [stats, setStats] = useState({ total: 0, cash: 0, bank: 0, count: 0 });

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amount || 0);

  const fetchCredits = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/credits/detailed`);
      setCredits(response.data);
      const total = response.data.reduce((sum, c) => sum + c.amount, 0);
      const cash = response.data.filter(c => c.credit_type === 'cash').reduce((sum, c) => sum + c.amount, 0);
      const bank = response.data.filter(c => c.credit_type === 'bank').reduce((sum, c) => sum + c.amount, 0);
      setStats({ total, cash, bank, count: response.data.length });
    } catch (error) { toast.error('Failed to load credits'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCredits(); }, []);

  const handleEdit = (credit) => {
    setEditingCredit(credit);
    setEditForm({ ...credit });
  };

  const handleUpdate = async () => {
    try {
      if (editingCredit.credit_id) {
        await axios.put(`${API}/credits/${editingCredit.credit_id}`, editForm);
      } else {
        await axios.put(`${API}/credits`, editForm, { params: { date: editingCredit.date, invoice_number: editingCredit.invoice_number } });
      }
      toast.success('Credit updated!');
      setEditingCredit(null);
      fetchCredits();
    } catch (error) { toast.error('Failed to update credit'); }
  };

  const handleDelete = async (credit) => {
    if (!window.confirm(`Delete credit ${credit.invoice_number || credit.credit_id}?`)) return;
    try {
      if (credit.credit_id) {
        await axios.delete(`${API}/credits/${credit.credit_id}`);
      } else {
        await axios.delete(`${API}/credits`, { params: { date: credit.date, invoice_number: credit.invoice_number } });
      }
      toast.success('Credit deleted!');
      fetchCredits();
    } catch (error) { toast.error('Failed to delete credit'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f7fa] to-[#e8ecf1] p-6" data-testid="credits-detail-view">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={onBack} className="premium-button"><ArrowLeft size={20} className="mr-2" /> Back</Button>
          <h1 className="heading text-4xl font-bold gradient-text">Credits Details</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="premium-card stat-card p-6">
            <p className="text-sm text-gray-600 mb-1">Total Credits</p>
            <h3 className="currency text-3xl font-bold gradient-text">{formatCurrency(stats.total)}</h3>
          </Card>
          <Card className="premium-card stat-card p-6">
            <p className="text-sm text-gray-600 mb-1">Cash Credits</p>
            <h3 className="currency text-2xl font-bold text-green-600">{formatCurrency(stats.cash)}</h3>
          </Card>
          <Card className="premium-card stat-card p-6">
            <p className="text-sm text-gray-600 mb-1">Bank Credits</p>
            <h3 className="currency text-2xl font-bold text-blue-600">{formatCurrency(stats.bank)}</h3>
          </Card>
          <Card className="premium-card stat-card p-6">
            <p className="text-sm text-gray-600 mb-1">Total Entries</p>
            <h3 className="text-3xl font-bold gradient-text">{stats.count}</h3>
          </Card>
        </div>

        <Card className="premium-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="premium-table w-full" data-testid="credits-table">
              <thead>
                <tr>
                  <th className="px-4 py-4 text-left text-sm font-semibold">Invoice No</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">Type</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">Party</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">Date</th>
                  <th className="px-4 py-4 text-right text-sm font-semibold">Amount</th>
                  <th className="px-4 py-4 text-center text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {credits.length === 0 ? (
                  <tr><td colSpan={6} className="px-6 py-12 text-center text-gray-500">No credits found</td></tr>
                ) : credits.map((credit, i) => (
                  <tr key={credit.credit_id || i} className="hover:bg-gray-50" data-testid={`credit-row-${i}`}>
                    <td className="px-4 py-4 font-bold text-gray-900">{credit.invoice_number || '-'}</td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${credit.credit_type === 'cash' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                        {credit.credit_type === 'cash' ? 'CASH' : 'BANK'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <p className="font-medium text-gray-900 text-sm">{credit.party_name || '-'}</p>
                      {credit.reference_number && <p className="text-xs text-gray-500">Ref: {credit.reference_number}</p>}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">{credit.date}</td>
                    <td className="px-4 py-4 text-right"><span className="font-bold text-blue-600">{formatCurrency(credit.amount)}</span></td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(credit)} data-testid={`edit-credit-${i}`}><PencilSimple size={16} /></Button>
                        <Button size="sm" variant="outline" onClick={() => handleDelete(credit)} className="text-red-600 hover:bg-red-50" data-testid={`delete-credit-${i}`}><Trash size={16} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Dialog open={editingCredit !== null} onOpenChange={() => setEditingCredit(null)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle className="heading text-xl font-bold">Edit Credit</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-semibold">Invoice Number</label><Input value={editForm.invoice_number || ''} onChange={(e) => setEditForm({ ...editForm, invoice_number: e.target.value })} /></div>
              <div><label className="text-sm font-semibold">Amount</label><Input type="number" step="0.01" value={editForm.amount || ''} onChange={(e) => setEditForm({ ...editForm, amount: parseFloat(e.target.value) || 0 })} /></div>
            </div>
            <div>
              <label className="text-sm font-semibold">Credit Type</label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2"><input type="radio" value="cash" checked={editForm.credit_type === 'cash'} onChange={(e) => setEditForm({ ...editForm, credit_type: e.target.value })} /><span>Cash</span></label>
                <label className="flex items-center gap-2"><input type="radio" value="bank" checked={editForm.credit_type === 'bank'} onChange={(e) => setEditForm({ ...editForm, credit_type: e.target.value })} /><span>Bank</span></label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-semibold">Party Name</label><Input value={editForm.party_name || ''} onChange={(e) => setEditForm({ ...editForm, party_name: e.target.value })} /></div>
              <div><label className="text-sm font-semibold">Reference No</label><Input value={editForm.reference_number || ''} onChange={(e) => setEditForm({ ...editForm, reference_number: e.target.value })} /></div>
            </div>
            <div><label className="text-sm font-semibold">Remarks</label><Input value={editForm.remarks || ''} onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })} /></div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleUpdate} className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white"><Check size={16} className="mr-2" /> Save</Button>
              <Button onClick={() => setEditingCredit(null)} variant="outline" className="flex-1"><X size={16} className="mr-2" /> Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditsDetailView;
